import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Device } from 'app/models/device.model';
import { DeviceScreen, HeadsetAlignment, MonitorAction, PlotData } from 'app/models/monitor.model';
import { Patient } from 'app/models/patient.model';
import { Test } from 'app/models/test.model';
import { TestBundle } from 'app/models/test-bundle.model';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { DEVICE_SCREEN, TEST_ACTION } from 'app/constants/constants';
import { MonitorTestService } from 'app/core/services/monitor-test.service';

@Component({
    selector: 'app-monitor-layout',
    templateUrl: './monitor-layout.component.html',
    standalone: false
})
export class MonitorLayoutComponent {
    headsetAlignmentOsData: PlotData[] = [];
    headsetAlignmentOdData: PlotData[] = [];
    headsetAlignmentLayoutLeft;
    headsetAlignmentLayoutRight;
    headsetAlignmentRevision = 1;
    headsetAlignmentOverride = false;
    headsetAlignmentDone = false;

    windowChanged: EventEmitter<any> = new EventEmitter();
    resizeSubscription: Subscription;

    @ViewChild('monitorLayoutContainer') monitorLayoutContainer: ElementRef;
  
    @Input() test: Test;
    @Input() testLoading: boolean;
    @Input() patient: Patient;
    @Input() bundle: TestBundle;
    @Input() isRegionShown: boolean = false;
    @Input() currentRegion: number = null;
    @Input() screen: DeviceScreen;
    @Input() isTestDone: boolean;
    @Input() isTestSyncing: boolean;
    @Input() isSingleImage: boolean;
    @Input() isV2Streaming: boolean;
    @Input() streamable: boolean = true;

    @Input() deviceLoading: boolean;
    @Input() device: Device;
    @Input() batteryPercentage: number;

    @Input() streamPopupVisible: boolean;

    @Input() deviceActionLoading: boolean;

    @Input() isLoading: boolean;

    @Input() headsetAlignment: HeadsetAlignment;

    @Output() onActionClicked: EventEmitter<MonitorAction> = new EventEmitter();
    @Output() onStreamOpen: EventEmitter<void> = new EventEmitter();
    @Output() onStreamClose: EventEmitter<void> = new EventEmitter();
    @Output() onClose: EventEmitter<void> = new EventEmitter();
  
  constructor(public monitorTestService: MonitorTestService) {
  }

  ngOnInit() {
    this.headsetAlignmentOsData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
    this.headsetAlignmentOdData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
    this.headsetAlignmentLayoutLeft = { ...this.monitorTestService.headsetAlignmentLayout};
    this.headsetAlignmentLayoutRight = { ...this.monitorTestService.headsetAlignmentLayout};

    this.resizeSubscription = this.windowChanged.pipe(
      debounceTime(300),
      distinctUntilChanged())
      .subscribe(() => this.calculateDimensions());
  }

  ngAfterViewInit() {
    this.windowChanged.emit(this.getEvent());
  }

  ngOnDestroy() {
    this.resizeSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['headsetAlignment']) {
        const osData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
        const odData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
        const headsetAlignment = changes['headsetAlignment'].currentValue as HeadsetAlignment;
        if (headsetAlignment) {
            osData[0].x.push(-headsetAlignment.OS.x);
            osData[0].y.push(headsetAlignment.OS.y);
            osData[0].marker.color.push(headsetAlignment.OS.aligned ? 1 : 0);

            odData[0].x.push(-headsetAlignment.OD.x);
            odData[0].y.push(headsetAlignment.OD.y);
            odData[0].marker.color.push(headsetAlignment.OD.aligned ? 1 : 0);
        }
        this.headsetAlignmentOsData = osData;
        this.headsetAlignmentOdData = odData;

        this.windowChanged.emit(this.getEvent());
    }
    if (changes['screen'] 
      && changes['screen'].currentValue?.name === DEVICE_SCREEN.HEADSET_ALIGNMENT.value 
      && changes['screen'].currentValue !== changes['screen'].previousValue?.name) {
      this.headsetAlignmentDone = false;
      this.headsetAlignment = null;
      this.headsetAlignmentOsData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
      this.headsetAlignmentOdData = [{ ...this.monitorTestService.getHeadsetAlignmentInitialPlotData() }];
      this.headsetAlignmentOverride = false;
      this.windowChanged.emit(this.getEvent());
    }
  }

  public calculateDimensions() {
    const headsetAlignmentPlotContainer = this.monitorLayoutContainer.nativeElement.querySelector('.headset_alignment_plot_container');
    if (headsetAlignmentPlotContainer) {
        const dimensions = this.monitorTestService.getDoublePlotDimensions(this.monitorLayoutContainer.nativeElement, headsetAlignmentPlotContainer);

        this.headsetAlignmentLayoutLeft = { ...this.monitorTestService.headsetAlignmentLayout, ...dimensions };
        this.headsetAlignmentLayoutRight = { ...this.monitorTestService.headsetAlignmentLayout, ...dimensions };
    }
  }

  public getEvent() {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  }

  get isHeadsetAlignmentShown(): boolean {
    return this.screen?.name === DEVICE_SCREEN.HEADSET_ALIGNMENT.value && !this.headsetAlignmentDone;
  }

  get canConfirmHeadsetAlignment(): boolean {
    return this.headsetAlignmentOverride || this.headsetAlignment?.OD.aligned && this.headsetAlignment?.OS.aligned;
  }

  onHeadsetAlignmentConfirm() {
    this.onActionClicked.emit(TEST_ACTION.HEADSET_ALIGNMENT_DONE);
    this.headsetAlignmentDone = true;
  }

  public get nativeElement(): HTMLElement {
    return this.monitorLayoutContainer.nativeElement;
  }
}